/**
 * Per pay app: unconditional waiver after payment is recorded (F-7).
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchUnconditionalLienStatus,
  generateUnconditionalLien,
  sendLienDocToOwner,
  getLienDocPdfUrl,
  LIEN_DOC_TYPE_LABELS,
  type UnconditionalLienStatusRow,
} from '../../../api/lienDocs'
import { getToken } from '../../../api/client'
import { fetchProject } from '../../../api/projects'
import { useActionToast } from '../../../lib/useActionToast'

interface Props { projectId: number }

export default function UnconditionalWaiverSection({ projectId }: Props) {
  const qc = useQueryClient()
  const { showToast, Toast } = useActionToast()
  const [busyId, setBusyId] = useState<number | null>(null)

  const { data: rows = [], isPending } = useQuery({
    queryKey: ['unconditional-lien-status', projectId],
    queryFn: () => fetchUnconditionalLienStatus(projectId),
    staleTime: 10_000,
  })

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => fetchProject(projectId),
    staleTime: 30_000,
  })

  if (isPending) {
    return (
      <div style={{ marginTop: 20, fontSize: 12, color: 'var(--ink4)' }} data-testid="unconditional-lien-loading">
        Loading pay application waivers…
      </div>
    );
  }

  if (!rows.length) {
    return null;
  }

  async function handleGenerate(row: UnconditionalLienStatusRow) {
    setBusyId(row.pay_app_id)
    try {
      await generateUnconditionalLien(projectId, row.pay_app_id)
      showToast(`Unconditional waiver generated for Pay App #${row.app_number}`)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['unconditional-lien-status', projectId] }),
        qc.invalidateQueries({ queryKey: ['project-lien-docs', projectId] }),
      ])
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to generate waiver')
    } finally {
      setBusyId(null)
    }
  }

  async function handleSend(row: UnconditionalLienStatusRow) {
    if (!row.existing_lien_doc_id) return
    setBusyId(row.pay_app_id)
    try {
      const result = await sendLienDocToOwner(row.existing_lien_doc_id, project?.owner_email || undefined)
      showToast(result.sent ? `Sent to ${result.to || 'owner'}` : 'Email queued (dev mode)')
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to send waiver')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div style={{ marginTop: 20 }} data-testid="unconditional-lien-section">
      {Toast}
      <div className="pm-section-h">
        <div>
          <div className="pm-section-tt">Unconditional waivers</div>
          <div className="pm-section-st">After payment is recorded · explicit action only</div>
        </div>
      </div>
      <div className="panel">
        {rows.map((row) => {
          const label = LIEN_DOC_TYPE_LABELS[row.doc_type] ?? row.doc_type
          const busy = busyId === row.pay_app_id
          return (
            <div
              key={row.pay_app_id}
              style={{ padding: '12px 16px', borderBottom: '1px solid var(--line)' }}
              data-testid={`unconditional-lien-row-${row.pay_app_id}`}
              data-paid={row.paid ? 'true' : 'false'}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)' }}>
                Pay App #{row.app_number}{row.period_label ? ` — ${row.period_label}` : ''}
              </div>
              {!row.paid && row.blocked_reason && (
                <div
                  style={{ fontSize: 11, color: 'var(--ink4)', marginTop: 4 }}
                  data-testid={`unconditional-lien-blocked-${row.pay_app_id}`}
                >
                  {row.blocked_reason}
                </div>
              )}
              {row.paid && row.existing_lien_doc_id && (
                <div style={{ fontSize: 11, color: 'var(--green)', marginTop: 4 }}
                  data-testid={`unconditional-lien-exists-${row.pay_app_id}`}>
                  {label} on file (Lien waivers table below · doc #{row.existing_lien_doc_id})
                </div>
              )}
              {row.paid && !row.existing_lien_doc_id && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn b-dk btn-sm"
                    disabled={busy}
                    data-testid={`unconditional-lien-generate-${row.pay_app_id}`}
                    onClick={() => handleGenerate(row)}
                  >
                    {busy ? 'Working…' : 'Generate unconditional waiver'}
                  </button>
                </div>
              )}
              {row.paid && row.existing_lien_doc_id && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn b-ol btn-sm"
                    data-testid={`unconditional-lien-pdf-${row.pay_app_id}`}
                    onClick={() => window.open(
                      getLienDocPdfUrl(row.existing_lien_doc_id!, getToken() ?? ''),
                      '_blank',
                    )}
                  >
                    Download PDF
                  </button>
                  <button
                    type="button"
                    className="btn b-dk btn-sm"
                    disabled={busy}
                    data-testid={`unconditional-lien-send-${row.pay_app_id}`}
                    onClick={() => handleSend(row)}
                  >
                    {busy ? 'Sending…' : 'Send to owner'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
